import { Check, Eye, ShieldCheck } from 'lucide-react'
import type * as React from 'react'
import { cn } from '../lib/cn'
import type { Audience } from './audience-tabs'
import { Button } from './button'
import { Chip } from './chip'
import { DismissControl } from './dismiss-control'

export type CardStatus = 'pending' | 'running' | 'confirmed' | 'dismissed' | 'failed'

export interface ConfirmCardProps {
  /** Registry kind: send_reply, log_time, book_contractor, match_invoice … */
  kind: string
  title: React.ReactNode
  /** The frozen payload, human-readable. */
  children?: React.ReactNode
  /** "What the customer/contractor will see" (scope §6.2), always stated. */
  impact: { audience: Audience | 'vendor' | 'nobody'; text: string }
  executor?: 'riley' | 'patchbay'
  scrubReport?: string
  status?: CardStatus
  outcome?: string
  onConfirm?: () => void
  onEdit?: () => void
  /** One click dismisses with no reason. A chip or optional text is the recorded reason (#558). */
  onDismiss?: (reason?: string | null) => void
  className?: string
}

const IMPACT_LABEL: Record<ConfirmCardProps['impact']['audience'], string> = {
  customer: 'Customer sees',
  contractor: 'Contractor sees',
  vendor: 'Vendor sees',
  internal: 'Staff see',
  nobody: 'Nobody outside sees anything',
}

/**
 * Every write is a frozen card (scope §6.2): Confirm / Edit / Dismiss, `a` / `e` / `x` when the
 * card is focused, run once. The impact line is never optional.
 */
export function ConfirmCard({
  kind,
  title,
  children,
  impact,
  executor = 'patchbay',
  scrubReport,
  status = 'pending',
  outcome,
  onConfirm,
  onEdit,
  onDismiss,
  className,
}: ConfirmCardProps) {
  const pending = status === 'pending'
  return (
    <section
      // biome-ignore lint/a11y/noNoninteractiveTabindex: the card is focusable so a/e/x act on it
      tabIndex={0}
      data-card-kind={kind}
      data-status={status}
      aria-label={`Confirm card: ${kind}`}
      onKeyDown={(e) => {
        if (!pending || e.target !== e.currentTarget || e.metaKey || e.ctrlKey || e.altKey) return
        if (e.key === 'a') onConfirm?.()
        else if (e.key === 'e') onEdit?.()
        else if (e.key === 'x') onDismiss?.(null)
        else return
        e.preventDefault()
      }}
      className={cn(
        'rounded-lg border bg-card p-3.5 shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
        pending ? 'border-primary/40' : 'border-border opacity-90',
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-1.5">
            <Chip tone="outline" className="font-mono text-2xs">
              {kind}
            </Chip>
            <span className="text-2xs text-subtle">runs in {executor === 'riley' ? 'Riley' : 'Patchbay'}</span>
          </div>
          <h3 className="text-base font-semibold">{title}</h3>
        </div>
        {status === 'confirmed' ? (
          <Chip tone="success">
            <Check /> Done
          </Chip>
        ) : status === 'dismissed' ? (
          <Chip tone="neutral">Dismissed</Chip>
        ) : status === 'failed' ? (
          <Chip tone="danger">Failed</Chip>
        ) : status === 'running' ? (
          <Chip tone="riley">Running…</Chip>
        ) : null}
      </div>
      {children ? <div className="mt-1.5 text-sm text-foreground/85">{children}</div> : null}
      <p className="mt-2 flex items-start gap-1.5 text-sm" data-slot="impact">
        <Eye className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <span>
          <span className="font-medium">{IMPACT_LABEL[impact.audience]}</span>
          {impact.audience === 'nobody' ? null : <>: {impact.text}</>}
        </span>
      </p>
      {scrubReport ? (
        <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="size-3.5 text-success" /> {scrubReport}
        </p>
      ) : null}
      {outcome ? <p className="mt-1 text-xs text-muted-foreground">{outcome}</p> : null}
      {pending ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button variant="primary" onClick={onConfirm} title="Confirm (a)">
            Confirm
          </Button>
          {onEdit ? (
            <Button onClick={onEdit} title="Edit (e)">
              Edit
            </Button>
          ) : null}
          {onDismiss ? <DismissControl onDismiss={onDismiss} /> : null}
        </div>
      ) : null}
    </section>
  )
}
